import { useMemo } from 'react';
import { useEnabledModules } from '@/hooks/useEnabledModules';
import { useProviderSegment } from '@/hooks/useProviderSegment';
import { useShopStaffAccess } from '@/hooks/useShopStaffAccess';
import { useAuthStore } from '@/store/authStore';
import { buildProviderCapabilities, isProviderPathAllowed } from '@/lib/provider-capabilities.mjs';

export function useProviderCapabilities() {
  const staff = useShopStaffAccess();
  const { role, user } = useAuthStore();
  const provider = useProviderSegment(!staff.isStaff);
  const tenant = useEnabledModules();

  const capabilities = useMemo(
    () => buildProviderCapabilities({
      providerModules: staff.isStaff ? [] : provider.modules,
      tenantModules: tenant.enabledModules,
      moduleConfigs: tenant.moduleConfigs,
      hasTeam: !staff.isStaff && provider.isTeamBusiness,
      shopDeliveryFleet: !staff.isStaff && provider.shopDeliveryFleet,
      allowedWorkerRoles: !staff.isStaff ? provider.allowedWorkerRoles : [],
      requiresFieldOperations: !staff.isStaff && provider.fieldOperationModules.some(module => tenant.enabledModules.includes(module)
        && tenant.moduleConfigs[module]?.channels?.providerAdminPortal !== false),
    }),
    [staff.isStaff, provider.modules, provider.fieldOperationModules, provider.isTeamBusiness, provider.shopDeliveryFleet, provider.allowedWorkerRoles, tenant.enabledModules, tenant.moduleConfigs],
  );

  const discoveryRequired = (role === 'provideradmin' || role === 'providerstaff') && !!user;
  const staffDiscoveryLoading = discoveryRequired && !staff.isFetched && !staff.isError;
  const staffDiscoveryError = discoveryRequired && staff.isError;
  const staffStock = staff.isStaff && tenant.enabledModules.includes('shop') && tenant.moduleConfigs.shop?.channels?.providerAdminPortal !== false;
  const ready = !staffDiscoveryLoading && !staffDiscoveryError && tenant.ready && !tenant.isError && (staff.isStaff || provider.ready && !provider.isError);

  return {
    tenantModules: tenant.enabledModules,
    capabilities: { ...capabilities, staffStock },
    isStaff: staff.isStaff,
    staffDiscoveryLoading,
    staffDiscoveryError,
    ready,
    isLoading: staffDiscoveryLoading || (!staff.isStaff && provider.isLoading) || tenant.isLoading,
    isError: staffDiscoveryError || (!staff.isStaff && provider.isError) || tenant.isError,
    isPathAllowed: (path: string) => ready && (staff.isStaff ? staffStock && path.split(/[?#]/, 1)[0].replace(/\/$/, '') === '/business/staff-stock' : isProviderPathAllowed(path, capabilities)),
  };
}
